import { oc } from "@orpc/contract";
import { z } from "zod";
import { TASK_ASSIGNEE_TYPES, TASK_STATUSES } from "@erp/shared-types";
import { REMITTANCE_PORTAL_FETCH_REQUEST_OUTCOMES } from "@erp/workflows";
import { lenientEnum, listSortShape } from "../../schemas/common";
import { InboundEmailSchema } from "../accounts-payable/ap-invoices.contract";
import {
  InvoicedCustomerSchema,
  RemittanceChannelSchema,
  RemittanceLineItemSchema,
} from "./ar-remittances.contract";

/**
 * AR Cash Receipts RPC Contract
 *
 * Provides endpoints for syncing Plaid bank transactions, listing Plaid bank
 * transactions in the review queue, fetching receipt detail with AI reasoning,
 * and overriding customer assignment.
 *
 * Note: CashReceiptStatusSchema is intentionally separate from PaymentStatusSchema
 * (see RESEARCH pitfall #6 — Plaid bank transactions only use needs_review / matched).
 */

const UUIDSchema = z.uuid();
const DecimalStringSchema = z.string().regex(/^-?\d+(\.\d+)?$/, {
  message: "Must be a numeric string",
});

/**
 * Status values applicable to Plaid bank transactions.
 * Separate from the full PaymentStatusSchema — bank transactions start at needs_review
 * and transition to matched on successful match to an open AR payment.
 */
export const CashReceiptStatusSchema = z.enum(["needs_review", "matched"]);

/**
 * AI confidence levels from AiReasoningAudit
 */
export const ConfidenceLevelSchema = z.enum(["high", "medium", "low", "none"]);

/**
 * Pagination schema for cash receipt list responses
 */
export const CashReceiptPaginationSchema = z.object({
  total: z.int().nonnegative(),
  limit: z.int().positive(),
  offset: z.int().nonnegative(),
});

// ---------------------------------------------------------------------------
// Sync
// ---------------------------------------------------------------------------

export const SyncCashReceiptsInputSchema = z.object({
  dateFrom: z.string(),
  dateTo: z.string(),
});

const IngestionErrorSchema = z.object({
  transactionId: z.string(),
  amount: z.number(),
  date: z.string(),
  merchantName: z.string().nullable(),
  error: z.string(),
});

const ResolutionErrorSchema = z.object({
  bankTransactionId: z.string(),
  error: z.string(),
});

const MatchingErrorSchema = z.object({
  bankTransactionId: z.string(),
  error: z.string(),
});

export const SyncCashReceiptsResponseSchema = z.object({
  ingestion: z.object({
    created: z.int().nonnegative(),
    updated: z.int().nonnegative(),
    skipped: z.int().nonnegative(),
    errors: z.array(IngestionErrorSchema),
  }),
  resolution: z.object({
    resolved: z.int().nonnegative(),
    skipped: z.int().nonnegative(),
    errors: z.array(ResolutionErrorSchema),
  }),
  matching: z.object({
    matched: z.int().nonnegative(),
    unmatched: z.int().nonnegative(),
    errors: z.array(MatchingErrorSchema),
  }),
});

// ---------------------------------------------------------------------------
// List
// ---------------------------------------------------------------------------

/**
 * Columns the review queue can be sorted by. Restricted to direct
 * BankTransaction columns — customerName (a join with a denormalized fallback)
 * and bankDescription (derived from JSONB metadata) are not server-sortable.
 */
export const CashReceiptSortBySchema = z.enum([
  "paymentDate",
  "paymentAmount",
  "status",
]);

/**
 * Payment types the cash receipts list can filter on: Famous's decoded
 * DEPOSITTYPE (check / eft / cash / internal) plus the bank channels email
 * receipts carry (ach / wire). Matched case-insensitively against the same
 * value the list's `paymentType` shows.
 */
export const CashReceiptPaymentTypeFilterSchema = z.enum([
  "check",
  "eft",
  "ach",
  "wire",
  "cash",
  "internal",
]);

/**
 * Terminal outcomes of a log-cash-receipt task: how the receipt ended up
 * reflected (or deliberately not reflected) in the ledger of record. The
 * backend seeds these as the task type's outcome vocabulary.
 */
export const CashReceiptLogOutcomeSchema = z.enum([
  "keyed",
  "keyed_externally",
  "on_account",
  "duplicate",
  "not_ours",
  "rejected",
  "obsolete",
]);

/**
 * Whether a log task is one a fold cancelled as a duplicate: the work it stood
 * for was already covered by another task on the same cash (the survivor was
 * keyed, or already held the open task), so it says nothing about where the
 * receipt is. Such a task never becomes the receipt's current task, which is
 * otherwise its newest one, but it stays in the receipt's task history.
 * Without this, an email receipt folded into a receipt keyed earlier turns the
 * keyed receipt's badge into "Closed · Duplicate".
 *
 * A duplicate a person closes by hand completes the task rather than
 * cancelling it, so it is a real disposition and still counts.
 */
export function isFoldCancelledDuplicateLogTask(task: {
  status: string;
  outcome: string | null;
}): boolean {
  return (
    task.status === "cancelled" &&
    task.outcome === CashReceiptLogOutcomeSchema.enum.duplicate
  );
}

/**
 * Where a receipt is in being filed, from its current log-cash-receipt task
 * and whether a remittance is linked. Checked in this order:
 *
 * - `keyed` — the task completed as keyed (here or in the ERP directly).
 * - `closed` — the task closed with any other outcome (duplicate, on account,
 *   not AR cash, returned, obsolete).
 * - `needs_review` — a person must act: the task needs input, failed, or was
 *   closed without an outcome.
 * - `before_tracking` — dated before the org's tracking start date, with no
 *   task or one nobody has started: history, in neither the Open nor the
 *   Completed tab.
 * - `needs_remittance` — still open (or no task yet) and no remittance linked.
 * - `ready_for_entry` — still open (or no task yet) with a remittance linked.
 */
export const CASH_RECEIPT_LIFECYCLE_STATUSES = [
  "needs_review",
  "needs_remittance",
  "ready_for_entry",
  "keyed",
  "closed",
  "before_tracking",
] as const;
export const CashReceiptLifecycleStatusSchema = z.enum(
  CASH_RECEIPT_LIFECYCLE_STATUSES,
);

/** Lifecycle statuses that still need work — the list's Open tab. */
export const OPEN_CASH_RECEIPT_LIFECYCLE_STATUSES = [
  "needs_review",
  "needs_remittance",
  "ready_for_entry",
] as const satisfies readonly z.infer<
  typeof CashReceiptLifecycleStatusSchema
>[];

/** Lifecycle statuses that are finished — the list's Completed tab. */
export const COMPLETED_CASH_RECEIPT_LIFECYCLE_STATUSES = [
  "keyed",
  "closed",
] as const satisfies readonly z.infer<
  typeof CashReceiptLifecycleStatusSchema
>[];

/** The list's tabs, as lifecycle status groups. */
export const CashReceiptQueueSchema = z.enum(["open", "completed"]);

/** A receipt's lifecycle status, plus the closing outcome when it has one. */
export const CashReceiptLifecycleSchema = z.object({
  status: lenientEnum(CashReceiptLifecycleStatusSchema),
  /** The current task's outcome, for `keyed` / `closed`; raw value. */
  outcome: z.string().nullable(),
});

export const CASH_RECEIPT_SETTLEMENT_STATES = [
  "settled",
  "under",
  "over",
  "indeterminate",
] as const;

/**
 * Whether the remittances linked to a receipt explain all of its cash
 * (GEN-3091). Derived, never stored: the stated totals of the receipt's active
 * links (`ar_receipt_remittance_links`) against the receipt amount, to the cent.
 * - `settled`: they are equal.
 * - `under`: less is explained (including no links at all); stays in review.
 * - `over`: more is explained than arrived — likely a duplicate; for a person.
 * - `indeterminate`: no receipt amount, or a linked remittance states no total.
 */
export const CashReceiptSettlementSchema = z.object({
  state: lenientEnum(z.enum(CASH_RECEIPT_SETTLEMENT_STATES)),
  /** Sum of the active links' stated totals. */
  linkedTotal: DecimalStringSchema,
  activeLinkCount: z.number().int().nonnegative(),
});

/**
 * Task-status filter value: a task status, or `none` for receipts that have
 * never had a logging task. Matched against the receipt's *current* task —
 * its most recent one that a fold did not cancel as a duplicate (see
 * {@link isFoldCancelledDuplicateLogTask}), which is the open task whenever
 * one exists.
 */
export const CashReceiptTaskStatusFilterSchema = z.enum([
  ...TASK_STATUSES,
  "none",
]);

/** Sentinel assignee-filter value: the current task has no assignee. */
export const CASH_RECEIPT_TASK_UNASSIGNED = "unassigned";

/**
 * Task-assignee filter value: `user:<uuid>`, `agent:<uuid>`, or
 * {@link CASH_RECEIPT_TASK_UNASSIGNED} — the same tokens the task queue's
 * reassign picker uses.
 */
export const CashReceiptTaskAssigneeFilterSchema = z
  .string()
  .regex(
    /^(unassigned|(user|agent):[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/i,
    { message: "Must be unassigned, user:<uuid>, or agent:<uuid>" },
  );

export const ListCashReceiptsInputSchema = z.object({
  status: z.array(CashReceiptStatusSchema).optional(),
  paymentType: z.array(CashReceiptPaymentTypeFilterSchema).optional(),
  paymentDateFrom: z.string().optional(),
  paymentDateTo: z.string().optional(),
  customerId: z.array(UUIDSchema).optional(),
  paymentAmountMin: z.number().optional(),
  paymentAmountMax: z.number().optional(),
  /** Free-text search over customer, bank description, and amount. */
  search: z.string().optional(),
  /** Lifecycle status group (the list's tab); ANDed with `lifecycleStatus`. */
  queue: CashReceiptQueueSchema.optional(),
  /** Lifecycle statuses (OR'd together). */
  lifecycleStatus: z.array(CashReceiptLifecycleStatusSchema).optional(),
  /** Status of the receipt's current logging task (OR'd together). */
  taskStatus: z.array(CashReceiptTaskStatusFilterSchema).optional(),
  /** Assignee of the receipt's current logging task (OR'd together). */
  taskAssignee: z.array(CashReceiptTaskAssigneeFilterSchema).optional(),
  ...listSortShape(CashReceiptSortBySchema),
  limit: z.int().min(1).max(100).optional(),
  offset: z.int().min(0).optional(),
});

/**
 * The receipt's current log-cash-receipt task — the open one when it exists,
 * else the most recently closed, skipping fold-cancelled duplicates (see
 * {@link isFoldCancelledDuplicateLogTask}) — as the list row shows it.
 */
export const CashReceiptListTaskSchema = z.object({
  id: UUIDSchema,
  displayId: z.string().nullable(),
  status: lenientEnum(z.enum(TASK_STATUSES)),
  /** Raw outcome name; the UI owns the label. Null while open. */
  outcome: z.string().nullable(),
  assigneeType: lenientEnum(z.enum(TASK_ASSIGNEE_TYPES)).nullable(),
  assigneeUserId: UUIDSchema.nullable(),
  assigneeAgentDefinitionId: UUIDSchema.nullable(),
  dueDate: z.string().nullable(),
  completedAt: z.string().nullable(),
});

/** The newest person-written note on the receipt's activity timeline. */
export const CashReceiptListNoteSchema = z.object({
  body: z.string(),
  actorName: z.string().nullable(),
  occurredAt: z.string(),
});

export const CashReceiptListItemSchema = z.object({
  id: UUIDSchema,
  paymentDate: z.string().nullable(),
  paymentAmount: DecimalStringSchema.nullable(),
  customerName: z.string().nullable(),
  /**
   * The customer's name as the source ERP renders it (Famous shows
   * `Acme Foods (ACM)`). Display-only — `customerName` stays the raw value so
   * sorting, filtering, and export are unaffected. Null when the receipt has
   * no matched customer.
   */
  erpDisplayName: z.string().nullable(),
  customerId: UUIDSchema.nullable(),
  status: lenientEnum(CashReceiptStatusSchema),
  /**
   * Whether the receipt's linked payment has at least one remittance advice
   * attached — the list's real progress signal: a matched receipt without a
   * remittance still needs its keying detail found.
   */
  hasRemittance: z.boolean(),
  /** How far the receipt's linked remittances explain its cash. */
  settlement: CashReceiptSettlementSchema,
  /**
   * Whose invoices the receipt's remittances pay: the distinct customers of
   * the non-rejected remittance lines' invoices, the one the most cash is
   * applied to first (ties: more lines, then name). Empty when no
   * remittance is linked. `customerId` / `customerName` stay the payer — the
   * customer who sent the money, which need not be any of these.
   */
  invoicedCustomers: z.array(InvoicedCustomerSchema),
  /**
   * How the receipt was paid: Famous's decoded DEPOSITTYPE (check / eft /
   * cash / internal), or the bank channel an email/Plaid row carries (ach,
   * wire, …). Null when unknown. Raw value — the UI owns the label.
   */
  paymentType: z.string().nullable(),
  bankDescription: z.string().nullable(),
  confidence: lenientEnum(ConfidenceLevelSchema).nullable(),
  /** Where the receipt is in being filed; see CASH_RECEIPT_LIFECYCLE_STATUSES. */
  lifecycle: CashReceiptLifecycleSchema,
  /** Null when the receipt has never had a logging task. */
  logTask: CashReceiptListTaskSchema.nullable(),
  latestNote: CashReceiptListNoteSchema.nullable(),
});

export const ListCashReceiptsResponseSchema = z.object({
  receipts: z.array(CashReceiptListItemSchema),
  pagination: CashReceiptPaginationSchema,
});

// ---------------------------------------------------------------------------
// Get (detail)
// ---------------------------------------------------------------------------

export const GetCashReceiptInputSchema = z.object({
  id: UUIDSchema,
});

/** One active receipt ⇄ remittance link, with what the remittance states. */
export const CashReceiptRemittanceLinkSchema = z.object({
  linkId: UUIDSchema,
  remittanceAdviceId: UUIDSchema,
  /** How the link was made: `bank_memo`, `group_sum`, `manual`, … */
  method: z.string(),
  linkedBySource: z.string(),
  linkedAt: z.string(),
  /** The remittance's intake channel (`email`, `bank_memo`, `portal`, …). */
  channel: z.string().nullable(),
  statedTotal: z.string().nullable(),
  statedCustomerName: z.string().nullable(),
  statedPaymentDate: z.string().nullable(),
  receivedAt: z.string().nullable(),
});

// The row-level task/note projections are list-only: the detail panel loads
// the task through the work item and notes through the activity feed.
export const CashReceiptDetailSchema = CashReceiptListItemSchema.omit({
  logTask: true,
  latestNote: true,
}).extend({
  /**
   * Source system. `"manual"` rows were entered through the UI and are the
   * only ones the manual-entry endpoints allow to be edited; Plaid/Famous rows
   * are read-only.
   */
  originSystem: z.string().nullable(),
  /** Source-system id; for Famous receipts, the Famous receipt number (BANKACTIVITYIDX). */
  originId: z.string().nullable(),
  customerMatch: z.object({
    customerId: UUIDSchema.nullable(),
    customerName: z.string().nullable(),
    confidence: lenientEnum(ConfidenceLevelSchema).nullable(),
    reasoning: z.string().nullable(),
  }),
  bankTransaction: z
    .object({
      type: z.string().nullable(),
      accountId: z.string().nullable(),
      merchantName: z.string().nullable(),
      description: z.string().nullable(),
      /** Check number or EFT trace (Famous REFNO), when known. */
      referenceNumber: z.string().nullable(),
      /** The date written on the check, when known. */
      checkDate: z.string().nullable(),
      /** Famous deposit-slip number; several checks can share one. */
      depositNumber: z.string().nullable(),
      /**
       * The raw row extracted from the bank statement (email-ingested receipts
       * only), surfaced read-only so a reviewer can see exactly what was parsed
       * and correct the derived fields against it. Null for Plaid/Famous/manual.
       */
      extractedRow: z.record(z.string(), z.unknown()).nullable(),
      /**
       * The payer's memo (addenda, OBI, reference text) as the bank email's
       * extraction schema read it. Null when the receipt came from no bank
       * email or the schema has no memo field.
       */
      bankMemo: z.string().nullable(),
      /**
       * The cash receipt handler workflow's judgment of the memo: whether it
       * says what the payment covers (and so was submitted as a bank_memo
       * remittance). Null until the workflow has judged it.
       */
      bankMemoVerdict: z
        .object({
          explainsPayment: z.boolean(),
          confidence: z.number(),
          reason: z.string(),
          judgedAt: z.string(),
        })
        .nullable(),
    })
    .nullable(),
  /**
   * The ingested email this receipt came from, when email-sourced (or when a
   * Famous/Plaid receipt was enriched from a matching email). Null otherwise.
   */
  sourceEmail: InboundEmailSchema.nullable(),
  /**
   * An AR user's mark that this cash should have come with a remittance email
   * Gently never recorded. The cash metrics dashboard counts these as missed
   * remittances. Null when unmarked.
   */
  remittanceMissing: z
    .object({
      markedAt: z.string(),
      markedByName: z.string().nullable(),
    })
    .nullable(),
  /**
   * Remittances actively linked to this receipt in
   * `ar_receipt_remittance_links`, oldest first.
   */
  remittanceLinks: z.array(CashReceiptRemittanceLinkSchema),
});

export const GetCashReceiptResponseSchema = z.object({
  receipt: CashReceiptDetailSchema.nullable(),
});

// ---------------------------------------------------------------------------
// Override customer
// ---------------------------------------------------------------------------

export const OverrideCustomerInputSchema = z.object({
  receiptId: UUIDSchema,
  customerId: UUIDSchema,
  triggerRematch: z.boolean().optional(),
});

export const OverrideCustomerResponseSchema = z.object({
  receipt: CashReceiptDetailSchema,
  /** True when the change was logged for customer learning. */
  correctionRecorded: z.boolean().optional(),
});

// ---------------------------------------------------------------------------
// Delete
// ---------------------------------------------------------------------------

export const DeleteCashReceiptInputSchema = z.object({
  id: UUIDSchema,
});

export const DeleteCashReceiptResponseSchema = z.object({
  success: z.literal(true),
});

// ---------------------------------------------------------------------------
// Unlink / Re-match (reconciliation escape hatches)
// ---------------------------------------------------------------------------

export const UnlinkCashReceiptInputSchema = z.object({
  id: UUIDSchema,
});

export const RematchCashReceiptInputSchema = z.object({
  id: UUIDSchema,
});

export const SetCashReceiptRemittanceMissingInputSchema = z.object({
  id: UUIDSchema,
  /** True to mark the remittance missing, false to clear the mark. */
  missing: z.boolean(),
});

export const RequestCashReceiptPortalFetchInputSchema = z.object({
  id: UUIDSchema,
});

export const RequestCashReceiptPortalFetchResponseSchema = z.object({
  /** `requested`, or why no fetch was requested. */
  outcome: z.enum(REMITTANCE_PORTAL_FETCH_REQUEST_OUTCOMES),
  requested: z.boolean(),
  customerName: z.string().nullable(),
  /** When the fetch was requested (this call's, or the earlier one's). */
  requestedAt: z.string().nullable(),
  /** The outcome in plain words. */
  message: z.string(),
});

// ---------------------------------------------------------------------------
// Manual create / edit
// ---------------------------------------------------------------------------

export const CreateCashReceiptInputSchema = z.object({
  amount: DecimalStringSchema,
  transactionDate: z.string().nullable().optional(),
  merchantName: z.string().nullable().optional(),
  paymentChannel: z.string().nullable().optional(),
  /** Check number or EFT trace. */
  referenceNumber: z.string().max(255).nullable().optional(),
  customerId: UUIDSchema.nullable().optional(),
});

export const CreateCashReceiptResponseSchema = z.object({
  receipt: CashReceiptDetailSchema,
});

export const UpdateCashReceiptInputSchema = z.object({
  id: UUIDSchema,
  amount: DecimalStringSchema.nullable().optional(),
  transactionDate: z.string().nullable().optional(),
  merchantName: z.string().nullable().optional(),
  paymentChannel: z.string().nullable().optional(),
  /** Check number or EFT trace. */
  referenceNumber: z.string().max(255).nullable().optional(),
});

export const UpdateCashReceiptResponseSchema = z.object({
  receipt: CashReceiptDetailSchema,
});

// ---------------------------------------------------------------------------
// Work item (receipt + linked payment + remittances + candidates)
// ---------------------------------------------------------------------------

export const GetCashReceiptWorkItemInputSchema = z.object({
  id: UUIDSchema,
});

/**
 * A remittance advice reachable from the receipt's linked payment, with its
 * claimed line items — the invoices a keyer transcribes into the ledger of
 * record. Line items reuse the remittance-detail schema so the work panel
 * renders them with the same components as the remittance page.
 */
export const CashReceiptWorkItemRemittanceSchema = z.object({
  id: UUIDSchema,
  channel: RemittanceChannelSchema,
  receivedAt: z.string(),
  statedTotalAmount: DecimalStringSchema.nullable(),
  statedReferenceNo: z.string().nullable(),
  senderEmail: z.string().nullable(),
  /**
   * The ingested email this advice came from, shaped for the email viewer —
   * lets the receipt slide-in show the remittance email beside the keying
   * panel. Null for non-email channels (EDI, portal, manual).
   */
  sourceEmail: InboundEmailSchema.nullable(),
  /**
   * The document the advice was extracted from — what the slide-in shows for
   * an advice with no source email (a portal download, a bank memo).
   */
  documentId: UUIDSchema.nullable(),
  lineItems: z.array(RemittanceLineItemSchema),
});

/**
 * A remittance advice with no receipt link yet whose stated total matches the
 * receipt amount: a customer's claim that this money was sent, waiting for
 * someone to confirm it against the bank receipt.
 */
export const CashReceiptCandidateRemittanceSchema =
  CashReceiptWorkItemRemittanceSchema.extend({
    statedCustomerName: z.string().nullable(),
    statedPaymentDate: z.string().nullable(),
  });

/** The open log-cash-receipt task anchored to this receipt, when one exists. */
export const CashReceiptTaskSummarySchema = z.object({
  id: UUIDSchema,
  displayId: z.string().nullable(),
  status: z.string(),
  outcome: z.string().nullable(),
});

// ---------------------------------------------------------------------------
// Hand-file the logging task
// ---------------------------------------------------------------------------

export const CreateCashReceiptLogTaskInputSchema = z.object({
  receiptId: UUIDSchema,
  /** Defaults to the same title the automatic filing path builds. */
  title: z.string().trim().min(1).max(500).optional(),
  description: z.string().max(10000).optional(),
  priority: z.enum(["urgent", "high", "medium", "low"]).optional(),
  dueDate: z.coerce.date().optional(),
  /**
   * Overrides the task type's default assignee for this task only. Omit to
   * take the type default, which is how the automatic filing path behaves.
   */
  assigneeType: z.enum(["user", "agent"]).optional(),
  assigneeUserId: UUIDSchema.optional(),
  assigneeAgentDefinitionId: UUIDSchema.optional(),
});

export const CreateCashReceiptLogTaskResponseSchema = z.object({
  taskId: UUIDSchema,
  /**
   * False when the receipt already had an open logging task, which is
   * returned as-is rather than duplicated. The UI says so instead of
   * reporting a create that did not happen.
   */
  created: z.boolean(),
});

export const GetCashReceiptWorkItemResponseSchema = z.object({
  receipt: CashReceiptDetailSchema.nullable(),
  remittances: z.array(CashReceiptWorkItemRemittanceSchema),
  candidateRemittances: z.array(CashReceiptCandidateRemittanceSchema),
  task: CashReceiptTaskSummarySchema.nullable(),
});

// ---------------------------------------------------------------------------
// Bulk task actions (cash-receipts list selection)
// ---------------------------------------------------------------------------

/** One list page at most — the selection never spans pages. */
const BulkReceiptIdsSchema = z.array(UUIDSchema).min(1).max(100);

/**
 * File the logging task for every selected receipt that has no open one.
 * Receipts that already have an open task keep it, and its assignee.
 */
export const BulkCreateCashReceiptLogTasksInputSchema = z.object({
  receiptIds: BulkReceiptIdsSchema,
});

export const BulkCreateCashReceiptLogTasksResponseSchema = z.object({
  created: z.number().int().nonnegative(),
  /** Already had an open task. */
  existing: z.number().int().nonnegative(),
  /** Not found in the caller's organization. */
  notFound: z.number().int().nonnegative(),
});

/**
 * Close the open logging task of every selected receipt with one outcome.
 * `obsolete` cancels; every other outcome completes.
 */
export const BulkCloseCashReceiptLogTasksInputSchema = z.object({
  receiptIds: BulkReceiptIdsSchema,
  outcome: CashReceiptLogOutcomeSchema,
  note: z.string().trim().max(2000).optional(),
});

export const BulkCloseCashReceiptLogTasksResponseSchema = z.object({
  closed: z.number().int().nonnegative(),
  /** Had no open task to close. */
  skipped: z.number().int().nonnegative(),
  failed: z.array(z.object({ receiptId: UUIDSchema, message: z.string() })),
});

// ---------------------------------------------------------------------------
// Manual link (operator hand-pick)
// ---------------------------------------------------------------------------

export const LinkCashReceiptRemittanceInputSchema = z.object({
  id: UUIDSchema,
  remittanceAdviceId: UUIDSchema,
});

export const LinkCashReceiptResponseSchema = z.object({
  receipt: CashReceiptDetailSchema,
  /**
   * True when the linked remittance's stated total differs from the receipt's — the
   * link is still written (fees / partial deposits are legitimate), the flag
   * lets the UI warn.
   */
  amountMismatch: z.boolean(),
});

// ---------------------------------------------------------------------------
// Hand-pick several remittances for one receipt (GEN-3106)
// ---------------------------------------------------------------------------

export const GetCashReceiptHandPickInputSchema = z.object({
  id: UUIDSchema,
  /** Narrows candidates by payer name, reference, sender or amount. */
  search: z.string().trim().max(200).optional(),
});

/** A remittance not linked to any receipt that could explain part of this one. */
export const CashReceiptHandPickCandidateSchema = z.object({
  id: UUIDSchema,
  channel: lenientEnum(RemittanceChannelSchema),
  receivedAt: z.string(),
  statedTotalAmount: DecimalStringSchema,
  statedReferenceNo: z.string().nullable(),
  statedCustomerName: z.string().nullable(),
  statedPaymentDate: z.string().nullable(),
  senderEmail: z.string().nullable(),
});

export const GetCashReceiptHandPickResponseSchema = z.object({
  receiptAmount: DecimalStringSchema.nullable(),
  settlement: CashReceiptSettlementSchema,
  candidates: z.array(CashReceiptHandPickCandidateSchema),
  /**
   * The group matcher's single proposed set (GEN-3105), shown pre-selected
   * for a person to confirm. Empty when it has none.
   */
  proposedRemittanceAdviceIds: z.array(UUIDSchema),
  /**
   * When two same-source sets both add up to the receipt: each set, offered
   * for a person to choose; nothing is pre-selected.
   */
  proposalChoices: z.array(z.array(UUIDSchema)),
});

/** Most remittances one hand-pick save links. */
export const MAX_HAND_PICK_REMITTANCES = 25;

export const LinkCashReceiptRemittancesInputSchema = z.object({
  id: UUIDSchema,
  remittanceAdviceIds: z
    .array(UUIDSchema)
    .min(1)
    .max(MAX_HAND_PICK_REMITTANCES),
  /**
   * The person confirmed linking more than the receipt amount. Without it a
   * pick whose total exceeds the receipt is refused.
   */
  confirmOver: z.boolean().optional(),
});

export const LinkCashReceiptRemittancesResponseSchema = z.object({
  receipt: CashReceiptDetailSchema,
  linkedCount: z.number().int().nonnegative(),
});

export const UnlinkCashReceiptRemittanceInputSchema = z.object({
  id: UUIDSchema,
  linkId: UUIDSchema,
  reason: z.string().trim().min(1).max(500),
});

export const UnlinkCashReceiptRemittanceResponseSchema = z.object({
  receipt: CashReceiptDetailSchema,
});

// ---------------------------------------------------------------------------
// Activity timeline
// ---------------------------------------------------------------------------

/**
 * One entry in a receipt's activity feed. Kinds are namespaced
 * `cash_receipt.<kind>` and carried as a plain string rather than an enum so
 * an older client renders a kind it doesn't know as a generic row instead of
 * failing output validation.
 */
export const CashReceiptActivitySchema = z.object({
  /**
   * Real activities carry a UUID; the synthesized ingest entry carries a
   * `synthetic:` prefixed id, which no mutation will accept.
   */
  id: z.string(),
  kind: z.string(),
  body: z.string().nullable(),
  payload: z.record(z.string(), z.unknown()).nullable(),
  actorUserId: UUIDSchema.nullable(),
  /** Display name of the actor, or the system label. Null when neither. */
  actorName: z.string().nullable(),
  occurredAt: z.string(),
  updatedAt: z.string(),
  /**
   * Whether the requesting user may edit or delete this entry — their own
   * notes only. Computed per viewer so the UI never shows an affordance the
   * server would refuse.
   */
  editable: z.boolean(),
});

export const ListCashReceiptActivityInputSchema = z.object({
  receiptId: UUIDSchema,
});

export const ListCashReceiptActivityResponseSchema = z.object({
  activities: z.array(CashReceiptActivitySchema),
});

const NoteBodySchema = z.string().min(1).max(4000);

export const AddCashReceiptNoteInputSchema = z.object({
  receiptId: UUIDSchema,
  body: NoteBodySchema,
});

export const EditCashReceiptNoteInputSchema = z.object({
  receiptId: UUIDSchema,
  activityId: UUIDSchema,
  body: NoteBodySchema,
});

export const CashReceiptNoteResponseSchema = z.object({
  activity: CashReceiptActivitySchema,
});

export const DeleteCashReceiptNoteInputSchema = z.object({
  receiptId: UUIDSchema,
  activityId: UUIDSchema,
});

export const DeleteCashReceiptNoteResponseSchema = z.object({
  success: z.literal(true),
});

// ---------------------------------------------------------------------------
// Contract
// ---------------------------------------------------------------------------

/**
 * AR Cash Receipts RPC contract:
 * - sync (POST): trigger Plaid sync + pipeline for a date range
 * - list (GET): paginated review queue with optional status/date filters
 * - get (GET): enriched single receipt with AI reasoning and linked payment
 * - overrideCustomer (POST): update customer assignment and attempt re-match
 * - delete (POST): permanently remove a cash receipt (operator escape hatch
 *   for badly-extracted rows; unlinks any associated Payment first)
 */
export const arCashReceiptsContracts = {
  sync: oc
    .input(SyncCashReceiptsInputSchema)
    .output(SyncCashReceiptsResponseSchema)
    .route({
      method: "POST",
      summary: "Sync bank cash receipts for a date range",
      description:
        "Pulls bank-feed transactions between two dates, resolves each to a customer, and auto-matches them to open payments. Returns created, resolved, and matched counts with errors.",
    }),
  list: oc
    .input(ListCashReceiptsInputSchema)
    .output(ListCashReceiptsResponseSchema)
    .route({
      method: "GET",
      summary: "List cash receipts",
      description:
        "Paginated cash receipt review queue, filterable by match status, payment type, date, amount, customer, logging-task status or assignee, and free text. Each row shows confidence and latest note.",
    }),
  get: oc
    .input(GetCashReceiptInputSchema)
    .output(GetCashReceiptResponseSchema)
    .route({
      method: "GET",
      summary: "Get cash receipt details",
      description:
        "Returns one cash receipt with its customer match confidence and AI reasoning, linked remittances, raw bank transaction fields, and source email.",
    }),
  overrideCustomer: oc
    .input(OverrideCustomerInputSchema)
    .output(OverrideCustomerResponseSchema)
    .route({
      method: "POST",
      summary: "Change the customer on a cash receipt",
      description:
        "Reassigns a cash receipt to a chosen customer and logs the correction for customer-match learning. Optionally re-runs payment matching for the receipt.",
    }),
  delete: oc
    .input(DeleteCashReceiptInputSchema)
    .output(DeleteCashReceiptResponseSchema)
    .route({
      method: "POST",
      summary: "Delete a cash receipt",
      description:
        "Soft-deletes a badly extracted cash receipt and unlinks any matched payment. AI match history is kept.",
    }),
  unlink: oc
    .input(UnlinkCashReceiptInputSchema)
    .output(OverrideCustomerResponseSchema)
    .route({
      method: "POST",
      summary: "Unlink a cash receipt from its remittances",
      description:
        "Closes the receipt's remittance links and returns it to needs review. Required before editing a linked remittance's amount. Returns the refreshed receipt.",
    }),
  rematch: oc
    .input(RematchCashReceiptInputSchema)
    .output(OverrideCustomerResponseSchema)
    .route({
      method: "POST",
      summary: "Re-run matching for a cash receipt",
      description:
        "Re-matches one cash receipt against unlinked remittances by exact amount within the date window, using reference, customer and payer as tiebreaks, then against same-source remittance groups. Returns the refreshed receipt.",
    }),
  create: oc
    .input(CreateCashReceiptInputSchema)
    .output(CreateCashReceiptResponseSchema)
    .route({
      method: "POST",
      summary: "Create a manual cash receipt",
      description:
        "Records a hand-entered cash receipt with amount, date, payer name, payment channel, and optional customer. Also files a log-cash-receipt task for it.",
    }),
  update: oc
    .input(UpdateCashReceiptInputSchema)
    .output(UpdateCashReceiptResponseSchema)
    .route({
      method: "POST",
      summary: "Edit a cash receipt",
      description:
        "Corrects amount, date, payer name, or payment channel on a manually entered or email-extracted cash receipt. Bank-synced and ERP-synced receipts are read-only.",
    }),
  getWorkItem: oc
    .input(GetCashReceiptWorkItemInputSchema)
    .output(GetCashReceiptWorkItemResponseSchema)
    .route({
      method: "GET",
      summary: "Get cash receipt keying work item",
      description:
        "Returns a receipt with its linked payment's remittances and invoice line items, candidate payments and remittances to match, and its open logging task.",
    }),
  createLogTask: oc
    .input(CreateCashReceiptLogTaskInputSchema)
    .output(CreateCashReceiptLogTaskResponseSchema)
    .route({
      method: "POST",
      summary: "Create a logging task for a cash receipt",
      description:
        "Files a log-cash-receipt task with optional title, priority, due date, and assignee. If an open task already exists, returns it instead of creating another.",
    }),
  bulkCreateLogTasks: oc
    .input(BulkCreateCashReceiptLogTasksInputSchema)
    .output(BulkCreateCashReceiptLogTasksResponseSchema)
    .route({
      method: "POST",
      summary: "Create logging tasks for selected cash receipts",
      description:
        "Files a log-cash-receipt task for up to 100 selected receipts that lack an open one. Returns created, already-existing, and not-found counts.",
    }),
  bulkCloseLogTasks: oc
    .input(BulkCloseCashReceiptLogTasksInputSchema)
    .output(BulkCloseCashReceiptLogTasksResponseSchema)
    .route({
      method: "POST",
      summary: "Close logging tasks for selected cash receipts",
      description:
        "Closes the open logging task on up to 100 receipts with one outcome, such as keyed, on account, or duplicate, plus an optional note. Obsolete cancels.",
    }),
  linkRemittance: oc
    .input(LinkCashReceiptRemittanceInputSchema)
    .output(LinkCashReceiptResponseSchema)
    .route({
      method: "POST",
      summary: "Link a cash receipt to a remittance",
      description:
        "Links a receipt to a remittance advice a person picked. Amount differences are allowed and flagged in the response; the remittance takes the receipt's customer when it has none.",
    }),
  setRemittanceMissing: oc
    .input(SetCashReceiptRemittanceMissingInputSchema)
    .output(GetCashReceiptResponseSchema)
    .route({
      method: "POST",
      summary: "Mark a cash receipt's remittance missing",
      description:
        "Marks (or clears) that a cash receipt should have come with a remittance email Gently did not record. Marked receipts count as missed remittances on the AR cash metrics dashboard.",
    }),
  requestPortalFetch: oc
    .input(RequestCashReceiptPortalFetchInputSchema)
    .output(RequestCashReceiptPortalFetchResponseSchema)
    .route({
      method: "POST",
      summary: "Request a cash receipt's remittance from the customer's portal",
      description:
        "Asks the customer's remittance portal fetch workflow to download this receipt's remittance. Asks again every time it is called, so a person can retry a fetch that failed or found nothing. Requests nothing, and says why, when the receipt has no customer, its customer is not a portal customer, it already has a remittance linked, or no portal fetch workflow is set up for the customer.",
    }),
  getHandPick: oc
    .input(GetCashReceiptHandPickInputSchema)
    .output(GetCashReceiptHandPickResponseSchema)
    .route({
      method: "GET",
      summary: "Get remittances to hand-pick for a cash receipt",
      description:
        "Returns a receipt's active remittance links, its settlement, and unlinked remittances inside the match window that could explain part of it.",
    }),
  linkRemittances: oc
    .input(LinkCashReceiptRemittancesInputSchema)
    .output(LinkCashReceiptRemittancesResponseSchema)
    .route({
      method: "POST",
      summary: "Link several remittances to a cash receipt",
      description:
        "Writes one hand-pick link row per chosen remittance in one transaction. Refuses a total above the receipt amount unless confirmOver is set.",
    }),
  unlinkRemittance: oc
    .input(UnlinkCashReceiptRemittanceInputSchema)
    .output(UnlinkCashReceiptRemittanceResponseSchema)
    .route({
      method: "POST",
      summary: "Unlink a hand-picked remittance from a cash receipt",
      description:
        "Closes one hand-pick link row with a reason. The row stays as history. Returns the refreshed receipt.",
    }),
  listActivity: oc
    .input(ListCashReceiptActivityInputSchema)
    .output(ListCashReceiptActivityResponseSchema)
    .route({
      method: "GET",
      summary: "List cash receipt activity",
      description:
        "Returns one receipt's activity timeline, newest first: lifecycle events and user notes, each flagged editable when the caller wrote it.",
    }),
  addNote: oc
    .input(AddCashReceiptNoteInputSchema)
    .output(CashReceiptNoteResponseSchema)
    .route({
      method: "POST",
      summary: "Add a note to a cash receipt",
      description: "Posts a user note to the receipt's activity timeline.",
    }),
  editNote: oc
    .input(EditCashReceiptNoteInputSchema)
    .output(CashReceiptNoteResponseSchema)
    .route({
      method: "POST",
      summary: "Edit a cash receipt note",
      description:
        "Changes the text of a note the caller wrote on a receipt's activity timeline.",
    }),
  deleteNote: oc
    .input(DeleteCashReceiptNoteInputSchema)
    .output(DeleteCashReceiptNoteResponseSchema)
    .route({
      method: "POST",
      summary: "Delete a cash receipt note",
      description:
        "Removes a note the caller wrote from a receipt's activity timeline.",
    }),
} as const;

// Type exports
export type CashReceiptStatus = z.infer<typeof CashReceiptStatusSchema>;
export type CashReceiptSortBy = z.infer<typeof CashReceiptSortBySchema>;
export type ConfidenceLevel = z.infer<typeof ConfidenceLevelSchema>;
export type CashReceiptPagination = z.infer<typeof CashReceiptPaginationSchema>;
export type SyncCashReceiptsInput = z.infer<typeof SyncCashReceiptsInputSchema>;
export type SyncCashReceiptsResponse = z.infer<
  typeof SyncCashReceiptsResponseSchema
>;
export type ListCashReceiptsInput = z.infer<typeof ListCashReceiptsInputSchema>;
export type ListCashReceiptsResponse = z.infer<
  typeof ListCashReceiptsResponseSchema
>;
export type CashReceiptListItem = z.infer<typeof CashReceiptListItemSchema>;
export type CashReceiptSettlement = z.infer<typeof CashReceiptSettlementSchema>;
export type GetCashReceiptInput = z.infer<typeof GetCashReceiptInputSchema>;
export type GetCashReceiptResponse = z.infer<
  typeof GetCashReceiptResponseSchema
>;
export type CashReceiptDetail = z.infer<typeof CashReceiptDetailSchema>;
export type CashReceiptRemittanceLink = z.infer<
  typeof CashReceiptRemittanceLinkSchema
>;
export type OverrideCustomerInput = z.infer<typeof OverrideCustomerInputSchema>;
export type OverrideCustomerResponse = z.infer<
  typeof OverrideCustomerResponseSchema
>;
export type DeleteCashReceiptInput = z.infer<
  typeof DeleteCashReceiptInputSchema
>;
export type DeleteCashReceiptResponse = z.infer<
  typeof DeleteCashReceiptResponseSchema
>;
export type UnlinkCashReceiptInput = z.infer<
  typeof UnlinkCashReceiptInputSchema
>;
export type RematchCashReceiptInput = z.infer<
  typeof RematchCashReceiptInputSchema
>;
export type SetCashReceiptRemittanceMissingInput = z.infer<
  typeof SetCashReceiptRemittanceMissingInputSchema
>;
export type RequestCashReceiptPortalFetchResponse = z.infer<
  typeof RequestCashReceiptPortalFetchResponseSchema
>;
export type CreateCashReceiptInput = z.infer<
  typeof CreateCashReceiptInputSchema
>;
export type CreateCashReceiptResponse = z.infer<
  typeof CreateCashReceiptResponseSchema
>;
export type UpdateCashReceiptInput = z.infer<
  typeof UpdateCashReceiptInputSchema
>;
export type GetCashReceiptWorkItemInput = z.infer<
  typeof GetCashReceiptWorkItemInputSchema
>;
export type GetCashReceiptWorkItemResponse = z.infer<
  typeof GetCashReceiptWorkItemResponseSchema
>;
export type CreateCashReceiptLogTaskInput = z.infer<
  typeof CreateCashReceiptLogTaskInputSchema
>;
export type CreateCashReceiptLogTaskResponse = z.infer<
  typeof CreateCashReceiptLogTaskResponseSchema
>;
export type CashReceiptWorkItemRemittance = z.infer<
  typeof CashReceiptWorkItemRemittanceSchema
>;
export type CashReceiptLogOutcome = z.infer<typeof CashReceiptLogOutcomeSchema>;
export type CashReceiptLifecycleStatus = z.infer<
  typeof CashReceiptLifecycleStatusSchema
>;
export type CashReceiptLifecycle = z.infer<typeof CashReceiptLifecycleSchema>;
export type CashReceiptQueue = z.infer<typeof CashReceiptQueueSchema>;
export type CashReceiptTaskStatusFilter = z.infer<
  typeof CashReceiptTaskStatusFilterSchema
>;
export type CashReceiptListTask = z.infer<typeof CashReceiptListTaskSchema>;
export type CashReceiptListNote = z.infer<typeof CashReceiptListNoteSchema>;
export type BulkCreateCashReceiptLogTasksInput = z.infer<
  typeof BulkCreateCashReceiptLogTasksInputSchema
>;
export type BulkCreateCashReceiptLogTasksResponse = z.infer<
  typeof BulkCreateCashReceiptLogTasksResponseSchema
>;
export type BulkCloseCashReceiptLogTasksInput = z.infer<
  typeof BulkCloseCashReceiptLogTasksInputSchema
>;
export type BulkCloseCashReceiptLogTasksResponse = z.infer<
  typeof BulkCloseCashReceiptLogTasksResponseSchema
>;
export type CashReceiptTaskSummary = z.infer<
  typeof CashReceiptTaskSummarySchema
>;
export type CashReceiptCandidateRemittance = z.infer<
  typeof CashReceiptCandidateRemittanceSchema
>;
export type LinkCashReceiptRemittanceInput = z.infer<
  typeof LinkCashReceiptRemittanceInputSchema
>;
export type LinkCashReceiptResponse = z.infer<
  typeof LinkCashReceiptResponseSchema
>;
export type UpdateCashReceiptResponse = z.infer<
  typeof UpdateCashReceiptResponseSchema
>;
export type CashReceiptActivity = z.infer<typeof CashReceiptActivitySchema>;
export type ListCashReceiptActivityInput = z.infer<
  typeof ListCashReceiptActivityInputSchema
>;
export type ListCashReceiptActivityResponse = z.infer<
  typeof ListCashReceiptActivityResponseSchema
>;
export type AddCashReceiptNoteInput = z.infer<
  typeof AddCashReceiptNoteInputSchema
>;
export type EditCashReceiptNoteInput = z.infer<
  typeof EditCashReceiptNoteInputSchema
>;
export type CashReceiptNoteResponse = z.infer<
  typeof CashReceiptNoteResponseSchema
>;
export type DeleteCashReceiptNoteInput = z.infer<
  typeof DeleteCashReceiptNoteInputSchema
>;
export type DeleteCashReceiptNoteResponse = z.infer<
  typeof DeleteCashReceiptNoteResponseSchema
>;
export type GetCashReceiptHandPickInput = z.infer<
  typeof GetCashReceiptHandPickInputSchema
>;
export type GetCashReceiptHandPickResponse = z.infer<
  typeof GetCashReceiptHandPickResponseSchema
>;
export type CashReceiptHandPickCandidate = z.infer<
  typeof CashReceiptHandPickCandidateSchema
>;
export type LinkCashReceiptRemittancesInput = z.infer<
  typeof LinkCashReceiptRemittancesInputSchema
>;
export type LinkCashReceiptRemittancesResponse = z.infer<
  typeof LinkCashReceiptRemittancesResponseSchema
>;
export type UnlinkCashReceiptRemittanceInput = z.infer<
  typeof UnlinkCashReceiptRemittanceInputSchema
>;
export type UnlinkCashReceiptRemittanceResponse = z.infer<
  typeof UnlinkCashReceiptRemittanceResponseSchema
>;
